import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    TextField,
} from '@mui/material';
import { MaterialListType, MaterialPriceList } from 'models/material';
import { ApolloCache, Reference, useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { createMaterialPriceListTemplate } from 'apollo/templates/material';
import { useTranslation } from 'react-i18next';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { Currency } from 'models/country';
import CurrencyInput from 'components/shared/currency-input/currency-input.component';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';

type AddMaterialPriceListDialogProps = {
    dialogOpen: boolean;
    onCloseDialog: () => void;
    onAddMaterialPriceList: (priceList: MaterialPriceList) => void;
    materialPriceLists: MaterialPriceList[];
};
const AddMaterialPriceListDialog: FC<AddMaterialPriceListDialogProps> = ({
    dialogOpen,
    onCloseDialog,
    onAddMaterialPriceList,
    materialPriceLists,
}: AddMaterialPriceListDialogProps) => {
    const { t: translate } = useTranslation(['common', 'lab-settings', 'workflow']);
    const [priceListName, setPriceListName] = useState('');
    const [priceListNameError, setPriceListNameError] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentOrganization = currentOrgVar();
    const defaultCurrency = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.CURRENCY,
    );
    const [materialListType, setMaterialListType] = useState('NORMAL_PRICE_LIST');
    const [currency, setCurrency] = useState<Currency>({ code: defaultCurrency || '' } as Currency);
    const [standardCurrencyCode, setStandardCurrencyCode] = useState('');
    const [conversionRate, setConversionRate] = useState(0);
    const [isConversionRateValid, setIsConversionRateValid] = useState(true);

    useEffect(() => {
        if (dialogOpen && materialPriceLists) {
            const standardPriceList = materialPriceLists.find(
                (materialPriceList) => materialPriceList.isStandardPriceList,
            );
            if (standardPriceList) {
                setStandardCurrencyCode(standardPriceList.currency);
            }
        }
    }, [dialogOpen, materialPriceLists]);

    const [addMaterialPriceList] = useMutation(createMaterialPriceListTemplate, {
        variables: {
            materialPriceListName: priceListName,
            currency: currency.code,
            materialListType,
            conversionRate,
        },
        onCompleted: ({ createMaterialPriceList }) => {
            onAddMaterialPriceList(createMaterialPriceList);
            onCloseDialog();
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<MaterialPriceList>, { data: { createMaterialPriceList } }) {
            cache.modify({
                fields: {
                    materialPriceLists(pricelistRefs: Reference | (Reference | undefined)[], { toReference }) {
                        if (pricelistRefs instanceof Array) {
                            return [...pricelistRefs, toReference(createMaterialPriceList)];
                        }
                        return [pricelistRefs, toReference(createMaterialPriceList)];
                    },
                },
            });
        },
    });

    const handlePriceListNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPriceListNameError(false);
        if (!event.target.value || priceListNameAlreadyExist(event.target.value)) {
            setPriceListNameError(true);
        }
        setPriceListName(event.target.value);
    };

    const priceListNameAlreadyExist = (name: string) => {
        const priceListNames = materialPriceLists.map((priceList) =>
            priceList.materialPriceListName.replace(/\s/g, '').toLocaleLowerCase(),
        );
        if (priceListNames.includes(name.replace(/\s/g, '').toLocaleLowerCase())) {
            return true;
        }
        return false;
    };

    const handleListTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setMaterialListType((event.target as HTMLInputElement).value);
    };

    const onConversionRateChange = (conversionRate: string) => {
        setIsConversionRateValid(true);
        const rate = conversionRate.replace(',', '.');
        if (rate) {
            if (rate === '0') {
                setIsConversionRateValid(false);
            }
            setConversionRate(parseFloat(rate));
        } else {
            setIsConversionRateValid(false);
        }
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={dialogOpen}>
            <DialogTitle>{translate('lab-settings:ADD_PRICE_LIST')}</DialogTitle>
            <DialogContent>
                <Box mt={1} mb={2}>
                    <Grid container spacing={2}>
                        <Grid item md={6} sm={12}>
                            <FormControl fullWidth>
                                <TextField
                                    id="material-price-list-name"
                                    label={translate('lab-settings:PRICE_LIST_NAME')}
                                    required
                                    onChange={handlePriceListNameChange}
                                    error={priceListNameError}
                                    helperText={
                                        priceListNameError && !priceListName
                                            ? translate('workflow:VALIDATION:NAME')
                                            : priceListNameError &&
                                              translate('lab-settings:VALIDATION:PRICE_LIST_ALREADY_EXIST')
                                    }
                                />
                            </FormControl>
                        </Grid>
                        <Grid item md={6} sm={12}>
                            <CurrencyInput
                                currency={currency}
                                onCurrencySelect={(currencyObj) => {
                                    setCurrency(currencyObj);
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>
                {currency && currency.code !== standardCurrencyCode ? (
                    <Box mt={1} mb={2}>
                        <Grid container spacing={2}>
                            <Grid item md={6} sm={12}>
                                <FormControl fullWidth component="fieldset">
                                    <RadioGroup
                                        row
                                        name="list-type"
                                        value={materialListType}
                                        onChange={handleListTypeChange}
                                    >
                                        <FormControlLabel
                                            value="NORMAL_PRICE_LIST"
                                            control={<Radio color="secondary" />}
                                            label={translate('lab-settings:NORMAL_PRICE_LIST')}
                                        />
                                        <FormControlLabel
                                            value="CURRENCY_CONVERTED_PRICE_LIST"
                                            control={<Radio color="secondary" />}
                                            label={translate('lab-settings:CURRENCY_CONVERTED_PRICE_LIST')}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                ) : undefined}
                {materialListType && materialListType === MaterialListType.CURRENCY_CONVERTED_PRICE_LIST ? (
                    <Box mt={1} mb={2}>
                        <Grid container spacing={2}>
                            <Grid item md={6} sm={12}>
                                <FormControl fullWidth>
                                    <NumberOnlyTextField
                                        id="conversion-rate"
                                        size="small"
                                        label={translate('lab-settings:CURRENCY_CONVERSION_RATE')}
                                        onValueChange={onConversionRateChange}
                                        value={conversionRate}
                                        required
                                        error={!isConversionRateValid}
                                        helperText={
                                            !isConversionRateValid &&
                                            translate('lab-settings:VALIDATION:CURRENCY_CONVERSION_RATE')
                                        }
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                ) : undefined}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseDialog}>{translate('common:CANCEL')}</Button>
                <Button
                    variant="contained"
                    disabled={
                        !priceListName ||
                        priceListNameError ||
                        (currency && currency.code !== standardCurrencyCode && !materialListType) ||
                        !isConversionRateValid
                    }
                    onClick={() => addMaterialPriceList()}
                >
                    {translate('lab-settings:SAVE_PRICE_LIST')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddMaterialPriceListDialog;
